import React, { useEffect, useState } from "react";
import moment from "moment";
import { DocumentDuplicateIcon, LinkIcon } from "@heroicons/react/24/outline";

import Pagination from "../commons/Pagination";
import SkeletonCard from "../skeletons/SkeletonCard";
import { IconLoading } from "../../assets/icons";
import { useToast } from "../../context/toastContext";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import { baseAxios } from "../../apis";
import useAuthedApi from "../../hooks/useAuthedApi";

// Component to display statistics cards at the top of the tab
const StatCard = ({ title, loading, children }) => (
  <div className="flex flex-col justify-center rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
    <p className="m-0 !py-0 text-sm font-medium text-gray-500">{title}</p>
    <div className="m-0 !mt-1 !py-0 text-2xl font-semibold text-gray-900">
      {loading ? (
        <SkeletonCard
          type="text"
          lines={1}
          widths={["50px"]}
          heights={["20px"]}
        />
      ) : (
        children
      )}
    </div>
  </div>
);

// Main Component
const ReferralTab = ({ referralData, loading, page, setPage }) => {
  // --- Hooks and State ---
  const [referralCode, setReferralCode] = useState("");
  const [loadingCreateReferral, setLoadingCreateReferral] = useState(false);
  const { addToast } = useToast();
  const { authedPost } = useAuthedApi(); // Get the authedPost function

  useEffect(() => {
    if (referralData?.results?.referral_code) {
      setReferralCode(referralData.results.referral_code);
    }
  }, [referralData]);

  // --- Handlers ---
  const copyToClipboard = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast("success", message);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      addToast("error", "Could not copy to clipboard.");
    }
  };

  const handleCopyCode = () => {
    copyToClipboard(referralCode, "Referral code copied!");
  };

  const handleCopyLink = () => {
    const signupUrl = `${window.location.origin}/signup?ucode=${referralCode}`;
    copyToClipboard(signupUrl, "Referral link copied!");
  };

  const handleGenerateReferral = async () => {
    setLoadingCreateReferral(true);
    try {
      // Use the simplified authedPost function
      const { data } = await authedPost(
        "/api/generate-referral",
        {} // Empty payload
      );

      setReferralCode(data.referral_code);
      addToast("success", "Referral code generated successfully.");
    } catch (error) {
      console.error("Failed to generate referral code:", error);
      addToast("error", "Failed to generate referral code.");
    } finally {
      setLoadingCreateReferral(false);
    }
  };

  // --- Render ---
  const tableHeaders = [
    // { label: "UID", key: "uid" },
    { label: "Email", key: "email" },
    { label: "Join Date", key: "join_date" },
  ];

  const referralList = referralData?.results?.data || [];
  const totalReferrals = referralData?.count || 0;
  const totalPages = Math.ceil(totalReferrals / 10);

  return (
    <div>
      {/* --- Top Stat Cards --- */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <StatCard title="Total Referrals" loading={loading}>
          {totalReferrals}
        </StatCard>

        <div className="flex flex-col justify-center rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
          <p className="m-0 !py-0 text-sm font-medium text-gray-500">
            Referral Code
          </p>
          <div className="mt-1 flex h-full items-center">
            {loading ? (
              <div className="text-2xl">
                <SkeletonCard
                  type="text"
                  lines={1}
                  widths={["120px"]}
                  heights={["20px"]}
                />
              </div>
            ) : referralCode ? (
              <div className="flex flex-col md:flex-row w-full md:items-center justify-between gap-2 md:gap-4 mt-2 md:mt-0">
                <span className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
                  {referralCode}
                  <DocumentDuplicateIcon
                    className="h-6 w-6 cursor-pointer text-gray-500 hover:text-gray-800"
                    onClick={handleCopyCode}
                  />
                </span>
                <div className="flex items-center gap-2">
                  <button
                    className="flex items-center rounded-md !bg-[#007A78] !py-1.5 px-3 !text-[13px] !font-bold text-white"
                    onClick={handleCopyLink}
                  >
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Copy Link
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="flex w-fit items-center justify-center rounded-md !bg-[#007A78] !py-1 px-3 !text-[13px] !font-bold text-white"
                onClick={handleGenerateReferral}
                disabled={loadingCreateReferral}
              >
                {loadingCreateReferral ? (
                  <div className="animate-spin py-1.5">
                    <IconLoading className="h-[18px] w-[18px]" />
                  </div>
                ) : (
                  "Generate Code"
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- Referral List Table --- */}
      <div className="mb-6 text-xl font-bold">My Referral List</div>
      <div className="w-full overflow-x-auto rounded-xl border">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-[#eaecf0] bg-[#F2F4F8] text-xs font-medium text-[#667085]">
            <tr>
              {tableHeaders.map(({ label, key }) => (
                <th
                  key={key}
                  className="select-none p-4 !text-xs font-bold text-[#21272A]"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-sm text-[#101828]">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-[#f2f4f7] bg-white">
                  {/* <td className="p-4">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["100px"]}
                      heights={["10px"]}
                    />
                  </td> */}
                  <td className="p-4">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["150px"]}
                      heights={["10px"]}
                    />
                  </td>
                  <td className="p-4">
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["100px"]}
                      heights={["10px"]}
                    />
                  </td>
                </tr>
              ))
            ) : referralList.length > 0 ? (
              referralList.map((item, index) => (
                <tr
                  key={item.id || index}
                  className="border-b border-[#f2f4f7] bg-white"
                >
                  {/* <td className="p-4 text-sm !font-normal">{item.id}</td> */}
                  <td className="p-4 text-sm !font-normal">
                    {item.referrer_email}
                  </td>
                  <td className="p-4 text-sm !font-normal">
                    {moment(item.created_at).format("DD/MM/YYYY HH:mm")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={tableHeaders.length}
                  className="h-40 text-sm p-3 text-center text-gray-400"
                >
                  No data found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- Pagination --- */}
      <div className="mt-4 flex items-center justify-center md:mt-6">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default ReferralTab;
